import {DestroyRef, Injectable, TemplateRef, inject, signal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter} from 'rxjs';

/** Compact mobile headers are default; the classic override stays in this browser tab. */
@Injectable({providedIn: 'root'})
export class MobileEntityHeaderService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly storageKey = 'mobile-entity-header-mode';
  readonly enabled = signal(this.readPreference());
  readonly template = signal<TemplateRef<unknown> | null>(null);

  constructor() {
    this.applyUrl(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(event => this.applyUrl(event.urlAfterRedirects));
  }

  show(template: TemplateRef<unknown>): void {
    if (this.enabled()) this.template.set(template);
  }

  clear(template: TemplateRef<unknown>): void {
    // A previous route must not clear a new page's header during destruction.
    if (this.template() === template) this.template.set(null);
  }

  disable(): void {
    this.setEnabled(false);
    void this.router.navigate([], {
      queryParams: {mobileHeader: 'classic'}, queryParamsHandling: 'merge', replaceUrl: true,
    });
  }

  private applyUrl(url: string): void {
    const mode = this.router.parseUrl(url).queryParams['mobileHeader'];
    if (mode === 'compact' || mode === 'classic') this.setEnabled(mode === 'compact');
  }

  private setEnabled(enabled: boolean): void {
    this.enabled.set(enabled);
    if (!enabled) this.template.set(null);
    try {
      sessionStorage.setItem(this.storageKey, enabled ? 'compact' : 'classic');
    } catch { /* Private browsing can block storage; the URL still works. */ }
  }

  private readPreference(): boolean {
    try { return sessionStorage.getItem(this.storageKey) !== 'classic'; }
    catch { return true; }
  }
}
